import React from 'react';
import { StyleSheet, View } from 'react-native';

import { AnimatedViews, theme, useResponsive, VStack } from '@sg/garnish';

import { Layout } from '../Layout';
import { OrderStatusDivider } from '../OrderStatusDivider';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderStatusLoading = () => {
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.container}>
        <Layout.Container>
          <Layout.Info>
            <OrderInfoPlaceholder />
          </Layout.Info>

          <Layout.Map>
            <AnimatedViews.Pulse index={1} style={styles.map} />
          </Layout.Map>

          <Layout.Content>
            <OrderDetailsPlaceholder />
          </Layout.Content>
        </Layout.Container>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Layout.Container>
        <Layout.Map>
          <AnimatedViews.Pulse style={styles.map} />
        </Layout.Map>

        <Layout.SidePanel>
          <Layout.Info>
            <OrderInfoPlaceholder />
          </Layout.Info>

          <Layout.Content>
            <OrderDetailsPlaceholder />
          </Layout.Content>
        </Layout.SidePanel>
      </Layout.Container>
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const OrderInfoPlaceholder = () => {
  return (
    <AnimatedViews.Pulse>
      <View style={styles.title} />
      <View style={styles.orderNumber} />
      <View style={styles.wantedTime} />

      <View style={styles.tracker}>
        <View style={styles.trackerLine} />

        <View style={styles.trackerSteps}>
          <View style={styles.trackerStep} />
          <View style={styles.trackerStep} />
          <View style={styles.trackerStep} />
          <View style={styles.trackerStep} />
        </View>
      </View>
    </AnimatedViews.Pulse>
  );
};

const OrderDetailsPlaceholder = () => {
  return (
    <VStack gap={0} divider={<OrderStatusDivider />}>
      <AnimatedViews.Pulse index={2} style={styles.section}>
        <View style={styles.sectionHeading} />
        <View style={styles.addressLineShort} />
        <View style={styles.addressLine} />
        <View style={styles.addressLineLong} />
      </AnimatedViews.Pulse>

      <AnimatedViews.Pulse index={3} style={styles.section}>
        <View style={styles.lineItem}>
          <View style={styles.lineItemImage} />

          <View style={styles.lineItemDetails}>
            <View style={styles.lineItemName} />
            <View style={styles.lineItemDescription} />
          </View>

          <View style={styles.lineItemPrice} />
        </View>
      </AnimatedViews.Pulse>

      <AnimatedViews.Pulse index={4} style={styles.section}>
        <View style={styles.ledgerRow}>
          <View style={styles.ledgerLabel} />
          <View style={styles.ledgerValue} />
        </View>

        <View style={styles.ledgerRow}>
          <View style={styles.ledgerLabelShort} />
          <View style={styles.ledgerValue} />
        </View>

        <View style={styles.ledgerRow}>
          <View style={styles.ledgerLabel} />
          <View style={styles.ledgerValue} />
        </View>
      </AnimatedViews.Pulse>
    </VStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  map: {
    flex: 1,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  title: {
    width: '72%',
    height: 32,
    marginVertical: 6,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  orderNumber: {
    width: '56%',
    height: 14,
    marginTop: theme.spacing['3'],
    marginBottom: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  wantedTime: {
    width: '80%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  tracker: {
    marginTop: theme.spacing['6'],
    rowGap: theme.spacing['3'],
  },
  trackerLine: {
    height: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  trackerSteps: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  trackerStep: {
    width: 56,
    height: 10,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  section: {
    rowGap: theme.spacing['2'],
    paddingVertical: theme.spacing['6'],
  },
  sectionHeading: {
    width: 140,
    height: 16,
    marginBottom: theme.spacing['3'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressLineShort: {
    width: 56,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressLine: {
    width: 96,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressLineLong: {
    width: 136,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
  lineItemImage: {
    width: 88,
    height: 88,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItemDetails: {
    flex: 1,
    rowGap: theme.spacing['2'],
  },
  lineItemName: {
    width: '72%',
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItemDescription: {
    width: '48%',
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItemPrice: {
    width: 44,
    height: 14,
    alignSelf: 'flex-start',
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ledgerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['1'],
  },
  ledgerLabel: {
    width: 72,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ledgerLabelShort: {
    width: 40,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ledgerValue: {
    width: 48,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});
